import { PublicationLayoutProps } from "../../api/publications"
import { Link } from "@tanstack/react-router"
import { Blockquote, Box, Button, Card, Center, Container, Flex, Float, Grid, GridItem, Heading, Image, Spinner, Text, Link as LinkR, useBreakpointValue } from "@chakra-ui/react"
import PublicationPreview from "./PublicationPreview"

import jakob from "../../../public/assets/images/jakobmarkt-inserat.jpg"


const LayoutFirstBigAllImage = ({
    publications,
    isPending,
    title,
    category_img_path,
    handleLoadMore,
    isLoadingMore,
    showMore
}: PublicationLayoutProps) => {
    const [first, ...rest] = publications ?? []
    const isMobile = useBreakpointValue({ base: true, md: false })
    
    return (
        <>
            {isPending ? (
                <Center
                    position="fixed"
                    top="0"
                    left="0"
                    width="100vw"
                    height="100vh"
                    zIndex="10000"
                    pointerEvents="none"
                >
                    <Spinner size="lg" color="main" />
                </Center>
            ) : (
                <>
                    <Heading size="4xl" color="main" mb={5}>{title}</Heading>
                    {category_img_path &&
                        <Box
                            h="7em"
                            bgColor="mainOpacity"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Image src={category_img_path.replace(/-(LG)\./, "-SM.")} alt="Category image" h="80%" />
                        </Box>
                    }

                    {publications?.length != 0 ? (
                        <>
                        {first &&
                            <Link to="/$slug" params={{ slug: first.classic_id }}>
                                <Card.Root w="100%" my={10} data-testid="publication_card">
                                    <Card.Header pos="relative" p={0}>
                                        {first.lead_image ? (
                                            <Image
                                                src={isMobile ? first.lead_image.path.replace(/-(LG)\./, "-MD.") : first.lead_image.path}
                                                alt={first.lead_image.file_name}
                                                borderTopRadius="md"
                                                h="25em"
                                                objectFit="cover"
                                                objectPosition={`${first.lead_image.focal_point ? first.lead_image.focal_point[0] : 50}% ${first.lead_image.focal_point ? first.lead_image.focal_point[1] : 50}%`}
                                            />
                                        ) : (
                                            <Box borderTopRadius="md" h="25em" bgColor="main"></Box>
                                        )}

                                        <Float
                                            display="block"
                                            pos="absolute"
                                            top={3}
                                            left={-3}
                                            translate="none"
                                            backgroundColor="mainOpacity"
                                            color="white"
                                            px={5}
                                            py={2}
                                            w="fit-content"
                                            boxShadow="rgba(0, 0, 0, 0.35) 0px 5px 15px"
                                        >
                                            <Text hyphens="auto" data-testid="publication_card_subtitle" fontWeight="bold">
                                                {first.subtitle && first.subtitle != "" ? first.subtitle : first.fk_categories.at(0)?.name}
                                            </Text>
                                        </Float>

                                        <Box
                                            position="absolute"
                                            bottom="0"
                                            left="0"
                                            right="0"
                                            height="50%"
                                            bg="linear-gradient(
                                                0deg,
                                                rgba(0, 0, 0, 0.7) 0%,
                                                rgba(0, 0, 0, 0.55) 40%,
                                                rgba(0, 0, 0, 0.25) 75%,
                                                rgba(0, 0, 0, 0) 100%
                                            );"
                                            pointerEvents="none"
                                        />

                                        <Float
                                            display="block"
                                            pos="absolute"
                                            bottom={10}
                                            left={2}
                                            translate="none"
                                            color="secondary"
                                            h="fit-content"
                                            insetBlockStart="auto"
                                        >
                                            <Blockquote.Root borderInlineStartColor="main">
                                                <Blockquote.Content fontSize={{sm: "14pt", md: "24pt"}} hyphens="auto" data-testid="publication_card_title">{first.title}</Blockquote.Content>
                                            </Blockquote.Root>
                                        </Float>
                                    </Card.Header>
                                    <Card.Body
                                        gap="2"
                                        w="full"
                                        h="full"
                                        p={3}
                                    >
                                        {first.lead ? (
                                            <Card.Description
                                                lineClamp={5}
                                                hyphens="auto"
                                                data-testid="publication_card_desc"
                                                color="black"
                                            >
                                            {first.lead}
                                            </Card.Description>
                                        ) : (
                                            <Card.Description
                                                lineClamp={5}
                                                hyphens="auto"
                                                data-testid="publication_card_desc"
                                                color="black"
                                                dangerouslySetInnerHTML={{ __html: first.content }}
                                            />
                                        )}
                                    </Card.Body>
                                </Card.Root>
                            </Link>
                        }

                        {rest &&
                            <Grid
                                my={20}
                                templateColumns={{
                                    base: "repeat(1, 1fr)",
                                    lg: "repeat(2, 1fr)",
                                    xl: "repeat(2, 1fr)",
                                    "2xl": "repeat(4, 1fr)",
                                }}
                                gap="2em"
                                justifyItems="center"
                                justifyContent="center"
                            >
                                {rest.map((publication) => (
                                    <GridItem cursor="pointer" key={publication.id} w="full" h="full">
                                        <PublicationPreview publication={publication} />
                                    </GridItem>
                                ))}
                            </Grid>
                        }

                        {showMore && (
                            <Flex justifyContent="center">
                                <Button
                                    onClick={handleLoadMore}
                                    variant="secondary"
                                    textTransform="uppercase"
                                    mt="10"
                                    w="12em"
                                >
                                    {isLoadingMore ? <Spinner size="sm" /> : "Mehr Beiträge laden"}
                                </Button>
                            </Flex>
                        )}
                        </>
                    ) : (
                        <Container
                            h="30em"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                        >
                            <Text
                                fontSize="3xl"
                                color="main"
                                fontWeight="bold"
                                mb={4}
                            >
                                Hier gibt es noch keine Beiträge
                            </Text>
                        </Container>
                    )}
                </>
            )}

            <Flex direction="column" alignItems="center" hideFrom="md" my={32} gap={32}>
                <LinkR href="https://www.jakob-markt.ch/jakob-schuehparadies.html" target="_blank" w="100%" cursor="pointer"><Image src={jakob} alt="Jakob's Schuehparadies" /></LinkR>
            </Flex>
        </>
    )
}

export default LayoutFirstBigAllImage
